import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import {
  CHOOSE_A_FAULT_RUN,
  type FaultRunChoice,
  type FaultRunFaultLevel,
  getFaultRun,
  listFaultRunsForRevision,
} from "./faultLink";
import { ApiError, describeApiFieldError } from "./http";
import {
  faultTypeSchema,
  prospectiveCurrentSourceSchema,
  scpdKindSchema,
  shortCircuitCaseSchema,
  starterCoordinationStatusSchema,
  starterCoordinationTypeSchema,
  starterCoordinationWarningCodeSchema,
  startingMethodSchema,
} from "./starterCoordinationContract";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const RUNS_URL = "/api/v1/electrical/starter-coordination/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The four choices a starter row needs; the backend imposes no default (A20 (e)). */
export const REQUIRED_STARTER_CHOICES = [
  "starting_method",
  "required_coordination_type",
  "scpd_kind",
  "declared_coordination_type",
] as const;

// The fault-run link (selector list, fault level, the selector's sentence) is shared
// with the breaker selectivity study and lives in faultLink; re-exported here so the
// starter coordination form keeps one import.
export { CHOOSE_A_FAULT_RUN, getFaultRun, listFaultRunsForRevision };
export type { FaultRunChoice, FaultRunFaultLevel };

// One motor starter on the MCC bus (A20 (e)). The three certificate items may be
// left out: blank means NOT ESTABLISHED, never assumed - the engine then reports
// REVIEW_REQUIRED and skips the check that needs the item. The form must omit a
// blank item instead of sending a value.
export const starterRowRequestSchema = z
  .object({
    motor_code: z.string().trim().min(1).max(50),
    rated_power_kw: exactDecimalSchema,
    full_load_current_a: exactDecimalSchema,
    // The four choices are optional here only so a missing one is reported by the rule
    // below as a missing value ("... is required."), the way the PV form reports its
    // redundancy mode; the backend requires every one of them.
    starting_method: startingMethodSchema.optional(),
    required_coordination_type: starterCoordinationTypeSchema.optional(),
    scpd_kind: scpdKindSchema.optional(),
    scpd_rating_a: exactDecimalSchema,
    contactor_ac3_rating_a: exactDecimalSchema,
    overload_setting_min_a: exactDecimalSchema,
    overload_setting_max_a: exactDecimalSchema,
    declared_coordination_type: starterCoordinationTypeSchema.optional(),
    certified_iq_ka: exactDecimalSchema.optional(),
    test_voltage_v: exactDecimalSchema.optional(),
    certificate_reference: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .superRefine((row, ctx) => {
    for (const field of REQUIRED_STARTER_CHOICES) {
      if (row[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }

    // Mirrors the backend rule (schemas/starter_coordination.py): the overload range
    // reads min below max. Both are plain decimals here, so Number() compares them.
    if (Number(row.overload_setting_min_a) >= Number(row.overload_setting_max_a)) {
      ctx.addIssue({
        code: "custom",
        message: "overload_setting_min_a must be below overload_setting_max_a",
        path: ["overload_setting_max_a"],
      });
    }
  });

// A prospective current typed by the engineer: allowed, but not linked evidence,
// so the study is REVIEW_REQUIRED (A20 (d)).
export const typedProspectiveCurrentSchema = z
  .object({
    prospective_current_ka: exactDecimalSchema,
    system_nominal_voltage_v: exactDecimalSchema,
    fault_bus_code: z.string().trim().min(1).max(50),
  })
  .strict();

export const starterCoordinationRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    // Exactly one of the two: a stored EOS-04 fault run of the organization, or a typed
    // value. The rule below reports the pair the same way the backend does, and names a
    // blank or malformed run id with the selector's own sentence.
    fault_run_id: z.string().optional(),
    typed: typedProspectiveCurrentSchema.optional(),
    rows: z.array(starterRowRequestSchema).min(1).max(200),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    if ((values.fault_run_id === undefined) === (values.typed === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "give exactly one prospective current source: fault_run_id or typed",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }

    const codes = values.rows.map((row) => row.motor_code);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: "custom",
        message: "motor codes must be unique within a study",
        path: ["rows"],
      });
    }
  });

export const starterCoordinationWarningSchema = z
  .object({
    code: starterCoordinationWarningCodeSchema,
    message: z.string(),
  })
  .strict();

// One starter as checked: every input echoed, each check true / false / null (not
// made - the certificate item is not established, or the starting method is not DOL).
export const starterRowResponseSchema = z
  .object({
    motor_code: z.string(),
    rated_power_kw: z.string(),
    full_load_current_a: z.string(),
    starting_method: startingMethodSchema,
    required_coordination_type: starterCoordinationTypeSchema,
    declared_coordination_type: starterCoordinationTypeSchema,
    scpd_kind: scpdKindSchema,
    scpd_rating_a: z.string(),
    contactor_ac3_rating_a: z.string(),
    overload_setting_min_a: z.string(),
    overload_setting_max_a: z.string(),
    certified_iq_ka: z.string().nullable(),
    test_voltage_v: z.string().nullable(),
    certificate_reference: z.string().nullable(),
    // Iq - Ik'' (negative when the check failed); null without a certified Iq.
    iq_margin_ka: z.string().nullable(),
    iq_adequate: z.boolean().nullable(),
    coordination_type_adequate: z.boolean(),
    test_voltage_adequate: z.boolean().nullable(),
    contactor_adequate: z.boolean().nullable(),
    overload_range_adequate: z.boolean().nullable(),
    status: starterCoordinationStatusSchema,
    warnings: z.array(starterCoordinationWarningSchema),
  })
  .strict();

export const starterCoordinationResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    current_source: prospectiveCurrentSourceSchema,
    // The linked run, its case, fault type and bus exactly as the stored result holds
    // them; null run / case / type for a typed value.
    fault_run_id: z.string().uuid().nullable(),
    calculation_case: shortCircuitCaseSchema.nullable(),
    fault_type: faultTypeSchema.nullable(),
    fault_bus_code: z.string(),
    prospective_current_ka: z.string(),
    system_nominal_voltage_v: z.string(),
    rows: z.array(starterRowResponseSchema),
    status: starterCoordinationStatusSchema,
    warnings: z.array(starterCoordinationWarningSchema),
    jurisdiction_profile: jurisdictionProfileSchema,
    notes: z.string().nullable(),
  })
  .strict();

export const starterCoordinationRunCreateRequestSchema = z
  .object({
    study: starterCoordinationRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const starterCoordinationRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: starterCoordinationResponseSchema,
  })
  .strict();

export type StarterRowRequest = z.infer<typeof starterRowRequestSchema>;
export type StarterCoordinationRequest = z.infer<typeof starterCoordinationRequestSchema>;
export type StarterCoordinationWarning = z.infer<typeof starterCoordinationWarningSchema>;
export type StarterRowResponse = z.infer<typeof starterRowResponseSchema>;
export type StarterCoordinationResponse = z.infer<typeof starterCoordinationResponseSchema>;
export type StarterCoordinationRunCreateRequest = z.infer<
  typeof starterCoordinationRunCreateRequestSchema
>;
export type StarterCoordinationRunResponse = z.infer<
  typeof starterCoordinationRunResponseSchema
>;

function formatApiError(data: unknown, status: number, subject: string): string {
  return describeApiFieldError(data, status, `${subject} failed`);
}

function requestSignal(signal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(30_000);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

/**
 * Calculate a starter coordination study and persist it as a run revision.
 *
 * The request carries the whole run body, because a study's notes belong to the
 * run, not to the checks the engine makes. With a project revision id the run is
 * stored under that revision of the selected project - and a linked fault run must
 * then belong to the same revision; without one it belongs to no project. An
 * unchanged study returns the stored revision with HTTP 200 instead of a new one
 * (A11); both are valid here.
 */
export async function createStarterCoordinationRun(
  payload: StarterCoordinationRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<StarterCoordinationRunResponse> {
  const validatedPayload = starterCoordinationRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      formatApiError(data, response.status, "Starter coordination"),
      response.status,
    );
  }

  const parsed = starterCoordinationRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error(
      "Unexpected response from the KES Electrical OS starter coordination runs API.",
    );
  }

  return parsed.data;
}
